import ApplicationStatus from 'enums/MyClients/ApplicationStatus';
import { SortDirection } from 'enums/SortDirection';
import {
    getAuthHeaders,
    getLocalTimeHeader,
    getSearchParamsString,
} from './api';
import ClientSearchFilter from 'interfaces/MyClients/ClientSearchFilter';
import {
    handleResponseConversionToBlob,
    handleResponseConversionToJSON,
} from './utility';
import { EnrollmentApplication } from 'interfaces/EnrollmentApplication';
import { ScopeOfAppointmentApprovalRequest } from 'interfaces/ScopeOfAppointment';

const PAGE_SIZE = 15;

const getAgents = async (
    authToken: string,
    page: number,
    pageSize: number = PAGE_SIZE,
    searchTerm?: string
): Promise<{ rows: unknown[]; count: number }> => {
    const headers = getAuthHeaders(authToken);

    const parameterMap: { [key: string]: unknown } = {
        searchTerm,
        skip: (page ?? 0) * pageSize,
        top: pageSize,
    };

    const url = `api/Agents/GetAgents?${getSearchParamsString(parameterMap)}`;
    let rows = [],
        count = 0;
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers,
        });

        if (response?.status === 200) {
            const result = await response?.json();
            rows = result.data;
            count = result.count;
        }
    } catch (err) {
        console.error(err);
    }

    return { rows, count };
};
const toggleDirectPayAccess = async (
    authToken: string,
    agents: unknown,
    eligibilityStatus: boolean,
    authorizedSignature: string
): Promise<Response> => {
    const headers = getAuthHeaders(authToken);
    const url = `api/Agents/ToggleDirectPayAccess`;
    return fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
            agents,
            eligibilityStatus,
            authorizedSignature,
        }),
    });
};
const getPayrollAgents = async (
    authToken: string,
    page: number,
    pageSize: number = PAGE_SIZE,
    searchTerm?: string,
    sortField?: string,
    sortDirection?: SortDirection
): Promise<{ rows: unknown[]; count: number }> => {
    const headers = getAuthHeaders(authToken);

    const parameterMap: { [key: string]: unknown } = {
        fieldName: sortField,
        direction: sortDirection,
        searchTerm,
        skip: (page ?? 0) * pageSize,
        top: pageSize,
    };
    const url = `api/Agents/GetPayrollAgents?${getSearchParamsString(
        parameterMap
    )}`;
    let rows = [],
        count = 0;
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers,
        });

        if (response?.status === 200) {
            const result = await response?.json();
            rows = result.data;
            count = result.count;
        }
    } catch (err) {
        console.error(err);
    }

    return { rows, count };
};

const exportPayrollAgents = async (authToken: string, searchTerm?: string) => {
    const headers = getAuthHeaders(authToken);
    const url = `api/Agents/ExportPayrollAgents?${getSearchParamsString({
        searchTerm,
    })}`;
    return fetch(url, {
        method: 'GET',
        headers,
    });
};

const getCommissionableClients = async (
    authToken: string,
    page: number = 0,
    pageSize: number = PAGE_SIZE,
    searchTerm?: string,
    sortField?: string,
    sortDirection?: SortDirection
): Promise<{ rows: unknown[]; count: number }> => {
    const headers = getAuthHeaders(authToken);

    const parameterMap: { [key: string]: unknown } = {
        fieldName: sortField,
        direction: sortDirection,
        searchTerm,
        skip: page * pageSize,
        top: pageSize,
    };
    const url = `api/Clients/GetCommissionableClients?${getSearchParamsString(
        parameterMap
    )}`;
    let rows = [],
        count = 0;
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers,
        });

        if (response?.status === 200) {
            const result = await response?.json();
            rows = result.data;
            count = result.count;
        }
    } catch (err) {
        console.error(err);
    }

    return { rows, count };
};

const getAgent = async (authToken, agentId) => {
    const headers = getAuthHeaders(authToken);
    const url = `api/Agent/GetAgent?${getSearchParamsString({
        agentId,
    })}`;
    const response = fetch(url, {
        method: 'GET',
        headers,
    }).then((response) => response.json());
    return response;
};

const getClients = async (
    authToken: string,
    page: number,
    pageSize: number = PAGE_SIZE,
    filters?: ClientSearchFilter
): Promise<{ rows: unknown[]; count: number }> => {
    const headers = getAuthHeaders(authToken);

    const parameterMap: { [key: string]: unknown } = {
        skip: (page ?? 0) * pageSize,
        top: pageSize,
        ...filters,
    };

    const url = `api/Clients/GetClients?${getSearchParamsString(parameterMap)}`;
    let rows = [],
        count = 0;
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers,
        });

        if (response?.status === 200) {
            const result = await response?.json();
            rows = result.data;
            count = result.count;
        }
    } catch (err) {
        console.error(err);
    }

    return { rows, count };
};

const getClientsWithPcpInfo = async (
    authToken: string,
    page: number,
    pageSize: number = PAGE_SIZE,
    filters?: ClientSearchFilter
): Promise<{ rows: unknown[]; count: number }> => {
    const headers = getAuthHeaders(authToken);

    const parameterMap: { [key: string]: unknown } = {
        skip: (page ?? 0) * pageSize,
        top: pageSize,
        ...filters,
    };

    const url = `api/Clients/GetClientsWithPcpInfo?${getSearchParamsString(
        parameterMap
    )}`;
    let rows = [],
        count = 0;
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers,
        });

        if (response?.status === 200) {
            const result = await response?.json();
            rows = result.data;
            count = result.count;
        }
    } catch (err) {
        console.error(err);
    }

    return { rows, count };
};

const getApplications = async (
    authToken: string,
    page: number,
    pageSize: number = PAGE_SIZE,
    applicationType?: ApplicationStatus,
    timeframe?: string,
    searchTerm?: string
): Promise<{ rows: unknown[]; count: number }> => {
    const headers = getAuthHeaders(authToken);

    const parameterMap: { [key: string]: unknown } = {
        timeframe,
        searchTerm,
        skip: (page ?? 0) * pageSize,
        top: pageSize,
    };

    const url = `api/Applications/Get${
        applicationType ?? ''
    }Applications?${getSearchParamsString(parameterMap)}`;

    let rows = [],
        count = 0;
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers,
        });

        if (response?.status === 200) {
            const result = await response?.json();
            rows = result.data;
            count = result.count;
        }
    } catch (err) {
        console.error(err);
    }

    return { rows, count };
};
const getPendingApplications = async (
    authToken: string,
    page: number,
    pageSize: number = PAGE_SIZE,
    searchTerm?: string
): Promise<{ rows: unknown[]; count: number }> => {
    const headers = getAuthHeaders(authToken);

    const parameterMap: { [key: string]: unknown } = {
        searchTerm,
        skip: (page ?? 0) * pageSize,
        top: pageSize,
    };

    const url = `api/Applications/getPendingApplications?${getSearchParamsString(
        parameterMap
    )}`;

    let rows = [],
        count = 0;
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers,
        });

        if (response?.status === 200) {
            const result = await response?.json();
            rows = result.data;
            count = result.count;
        }
    } catch (err) {
        console.error(err);
    }

    return { rows, count };
};
const getScopeOfAppointments = async (
    authToken: string,
    page: number,
    pageSize: number = PAGE_SIZE,
    filter: ScopeOfAppointmentFilter = {}
): Promise<{ rows: unknown[]; count: number }> => {
    const headers = getAuthHeaders(authToken);

    const parameterMap: { [key: string]: unknown } = {
        ...filter,
        skip: (page ?? 0) * pageSize,
        top: pageSize,
    };

    const url = `api/Clients/getScopeOfAppointments?${getSearchParamsString(
        parameterMap
    )}`;

    let rows = [],
        count = 0;
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers,
        });

        if (response?.status === 200) {
            const result = await response?.json();
            rows = result.data;
            count = result.count;
        }
    } catch (err) {
        console.error(err);
    }

    return { rows, count };
};

type ScopeOfAppointmentFilter = {
    clientId?: string;
    hasMemberSigned?: boolean;
    isActive?: boolean;
};

type LinkedScopeDocument = {
    fileId: string;
    fileName: string;
    documentTitle: string;
    contentType?: string;
};

const getSignedScopeOfAppointmentDocument = async (
    authToken: string,
    clientId: string
): Promise<Blob> => {
    const headers = getAuthHeaders(authToken);
    const url = `api/Clients/GetSignedScopeOfAppointmentDocument?${getSearchParamsString(
        { clientId }
    )}`;
    return fetch(url, { method: 'GET', headers }).then(
        handleResponseConversionToBlob
    );
};

const linkSignedScopeOfAppointmentDocument = async (
    authToken: string,
    applicationId: string,
    clientId: string
): Promise<LinkedScopeDocument> => {
    const headers = getAuthHeaders(authToken);
    return fetch('api/Enrollment/LinkSignedScopeOfAppointmentDocument', {
        method: 'POST',
        headers,
        body: JSON.stringify({ applicationId, clientId }),
    }).then(handleResponseConversionToJSON);
};

const saveClientScopeOfAppointment = async (
    authToken: string,
    request: any
): Promise<unknown> => {
    const headers = getAuthHeaders(authToken);

    const url = `api/Clients/SaveScopeOfAppointment`;

    return await fetch(url, {
        method: 'POST',
        body: JSON.stringify(request),
        headers: {
            ...headers,
        },
    }).then(handleResponseConversionToJSON);
};
const getApplication = async (
    authToken: string,
    applicationId: string
): Promise<unknown> => {
    const headers = getAuthHeaders(authToken);

    const url = `api/Applications/GetApplication?${getSearchParamsString({
        applicationId,
    })}`;

    let data = {};
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers,
        });

        if (response?.status === 200) {
            data = await response?.json();
        }
    } catch (err) {
        console.error(err);
    }

    return data;
};

const getMemberCard = async (
    authToken: string,
    memberId: string,
    companyCode: string
): Promise<unknown> => {
    const headers = getAuthHeaders(authToken);

    const url = `api/Clients/GetMemberCard?${getSearchParamsString({
        memberId,
        companyCode,
    })}`;

    return await fetch(url, {
        method: 'GET',
        headers,
    }).then(handleResponseConversionToJSON);
};

const shareMemberCard = async (
    authToken: string,
    memberId: string,
    companyCode: string,
    memberName: string,
    channels: string[],
    email?: string,
    phoneNumber?: string
): Promise<unknown> => {
    const headers = getAuthHeaders(authToken);

    return await fetch('api/Clients/ShareMemberCard', {
        method: 'POST',
        headers,
        body: JSON.stringify({
            memberId,
            companyCode,
            memberName,
            channels,
            email,
            phoneNumber,
        }),
    });
};

const getBenefitEnrollments = async (
    authToken: string,
    page: number,
    pageSize: number = PAGE_SIZE,
    searchTerm?: string
): Promise<{ rows: unknown[]; count: number }> => {
    const headers = getAuthHeaders(authToken);

    const parameterMap: { [key: string]: unknown } = {
        searchTerm,
        skip: (page ?? 0) * pageSize,
        top: pageSize,
    };

    const url = `api/BenefitEnrollments/GetBenefitEnrollments?${getSearchParamsString(
        parameterMap
    )}`;
    let rows = [],
        count = 0;
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers,
        });

        if (response?.status === 200) {
            const result = await response?.json();
            rows = result.data;
            count = result.count;
        }
    } catch (err) {
        console.error(err);
    }

    return { rows, count };
};

const getHealthRiskAssessments = async (
    authToken: string,
    page: number,
    pageSize: number = PAGE_SIZE,
    searchTerm?: string
): Promise<{ rows: unknown[]; count: number }> => {
    const headers = getAuthHeaders(authToken);

    const parameterMap: { [key: string]: unknown } = {
        searchTerm,
        skip: (page ?? 0) * pageSize,
        top: pageSize,
    };

    const url = `api/HealthRiskAssessment/GetHealthRiskAssessments?${getSearchParamsString(
        parameterMap
    )}`;
    let rows = [],
        count = 0;
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers,
        });

        if (response?.status === 200) {
            const result = await response?.json();
            rows = result.data;
            count = result.count;
        }
    } catch (err) {
        console.error(err);
    }

    return { rows, count };
};

const getPaperApplicationsMetadata = async (
    authToken: string,
    page: number,
    pageSize: number = PAGE_SIZE,
    searchTerm?: string
): Promise<{ rows: unknown[]; count: number }> => {
    const headers = getAuthHeaders(authToken);

    const parameterMap: { [key: string]: unknown } = {
        searchTerm,
        skip: (page ?? 0) * pageSize,
        top: pageSize,
    };

    const url = `api/PaperApplication/GetPaperApplicationsMetadata?${getSearchParamsString(
        parameterMap
    )}`;
    let rows = [],
        count = 0;
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers,
        });

        if (response?.status === 200) {
            const result = await response?.json();
            rows = result.data;
            count = result.count;
        }
    } catch (err) {
        console.error(err);
    }

    return { rows, count };
};

const getPendingApplicationData = async (
    authToken: string,
    clientId: string
): Promise<EnrollmentApplication> => {
    const headers = getAuthHeaders(authToken);

    const url = `api/Applications/GetPendingApplicationData?${getSearchParamsString(
        {
            clientId,
        }
    )}`;

    return await fetch(url, {
        method: 'GET',
        headers,
    }).then(handleResponseConversionToJSON);
};

const getPendingApplicationStatus = async (
    authToken: string,
    clientId: string
): Promise<{ status: string }> => {
    const headers = getAuthHeaders(authToken);

    const url = `api/Applications/GetPendingApplicationStatusForAgent?${getSearchParamsString(
        {
            clientId,
        }
    )}`;

    return await fetch(url, {
        method: 'GET',
        headers,
    }).then(handleResponseConversionToJSON);
};

const cancelPendingApplication = async (
    authToken: string,
    clientId: string
): Promise<boolean> => {
    const headers = getAuthHeaders(authToken);

    const url = `api/Applications/CancelPendingApplication?${getSearchParamsString(
        {
            clientId,
        }
    )}`;

    return await fetch(url, {
        method: 'POST',
        headers: {
            ...headers,
            ...getLocalTimeHeader(),
        },
    }).then(handleResponseConversionToJSON);
};

const getClientScopeOfAppointment = async (clientId?: string): Promise<any> => {
    const url = `api/Member/GetScopeOfAppointment?${getSearchParamsString({
        clientId,
    })}`;

    return await fetch(url, {
        method: 'GET',
    }).then(handleResponseConversionToJSON);
};
const getClientScopeOfAppointmentStatus = async (
    clientId?: string
): Promise<any> => {
    const url = `api/Member/GetScopeOfAppointmentStatus?${getSearchParamsString(
        {
            clientId,
        }
    )}`;

    return await fetch(url, {
        method: 'GET',
    }).then(handleResponseConversionToJSON);
};
const acceptScopeOfAppointment = async (
    request: ScopeOfAppointmentApprovalRequest
): Promise<any> => {
    const url = `api/Member/AcceptScopeOfAppointment`;

    return await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
    });
};

export {
    getAgent,
    getAgents,
    getPayrollAgents,
    exportPayrollAgents,
    getClients,
    getClientsWithPcpInfo,
    getMemberCard,
    getApplication,
    getApplications,
    getBenefitEnrollments,
    getHealthRiskAssessments,
    getPaperApplicationsMetadata,
    toggleDirectPayAccess,
    getPendingApplications,
    getCommissionableClients,
    shareMemberCard,
    getPendingApplicationData,
    getPendingApplicationStatus,
    cancelPendingApplication,
    getScopeOfAppointments,
    getSignedScopeOfAppointmentDocument,
    linkSignedScopeOfAppointmentDocument,
    saveClientScopeOfAppointment,

    // Scope of Appointment Review
    getClientScopeOfAppointment,
    getClientScopeOfAppointmentStatus,
    acceptScopeOfAppointment,
};
